import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import { Info } from 'lucide-react'
import { cn } from 'lib/utils'

const POPOVER_WIDTH = 256
const VIEWPORT_PADDING = 8

function computePopoverPosition(triggerRect: DOMRect, popoverHeight = 0): React.CSSProperties {
  const viewportWidth = window.innerWidth
  const viewportHeight = window.innerHeight

  let left = triggerRect.left
  let top = triggerRect.bottom + 6

  if (left + POPOVER_WIDTH > viewportWidth - VIEWPORT_PADDING) {
    left = viewportWidth - POPOVER_WIDTH - VIEWPORT_PADDING
  }
  if (left < VIEWPORT_PADDING) {
    left = VIEWPORT_PADDING
  }

  if (popoverHeight > 0 && top + popoverHeight > viewportHeight - VIEWPORT_PADDING) {
    top = triggerRect.top - popoverHeight - 6
  }
  if (top < VIEWPORT_PADDING) {
    top = VIEWPORT_PADDING
  }

  return { top, left, width: POPOVER_WIDTH }
}

interface KpiInfoButtonProps {
  description: string
  ariaLabel?: string
}

export function KpiInfoButton({ description, ariaLabel = 'Information' }: KpiInfoButtonProps) {
  const [open, setOpen] = useState(false)
  const [popoverStyle, setPopoverStyle] = useState<React.CSSProperties>({})
  const containerRef = useRef<HTMLDivElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)
  const tooltipId = React.useId()

  useLayoutEffect(() => {
    if (!open || !containerRef.current || !popoverRef.current) return

    const triggerRect = containerRef.current.getBoundingClientRect()
    const popoverRect = popoverRef.current.getBoundingClientRect()
    setPopoverStyle(computePopoverPosition(triggerRect, popoverRect.height))
  }, [open, description])

  const showPopover = () => {
    if (containerRef.current) {
      setPopoverStyle(computePopoverPosition(containerRef.current.getBoundingClientRect()))
    }
    setOpen(true)
  }

  const hidePopover = () => {
    setOpen(false)
  }

  useEffect(() => {
    if (!open) return

    const handleScrollOrResize = () => hidePopover()

    window.addEventListener('resize', handleScrollOrResize)
    window.addEventListener('scroll', handleScrollOrResize, true)
    return () => {
      window.removeEventListener('resize', handleScrollOrResize)
      window.removeEventListener('scroll', handleScrollOrResize, true)
    }
  }, [open])

  return (
    <div
      className="relative shrink-0"
      ref={containerRef}
      onMouseEnter={showPopover}
      onMouseLeave={hidePopover}
      onFocus={showPopover}
      onBlur={hidePopover}
    >
      <span
        role="img"
        tabIndex={0}
        aria-label={ariaLabel}
        aria-describedby={open ? tooltipId : undefined}
        className={cn(
          'inline-flex cursor-default text-muted-foreground hover:text-foreground focus:outline-none focus-visible:text-foreground',
          open && 'text-foreground',
        )}
      >
        <Info className="h-3.5 w-3.5" strokeWidth={2} />
      </span>
      {open
        ? ReactDOM.createPortal(
            <div
              ref={popoverRef}
              id={tooltipId}
              role="tooltip"
              style={popoverStyle}
              className="pointer-events-none fixed z-[200] rounded-md border border-border bg-popover p-3 text-xs leading-relaxed text-popover-foreground shadow-md"
            >
              {description}
            </div>,
            document.body,
          )
        : null}
    </div>
  )
}
